// src/pages/CommGross.jsx
// Separate gross-sales commission page. Existing /commissions remains untouched.

import React from 'react';

import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import CommGrossCommissionsTable from '../components/CommGross/CommGrossCommissionsTable.jsx';
import CommGrossMonthlyCommissionsTable from '../components/CommGross/CommGrossMonthlyCommissionsTable.jsx';
import CommGrossMonthlyGrossSalesTable from '../components/CommGross/CommGrossMonthlyGrossSalesTable.jsx';
import CommGrossQuotaBreakdownTable from '../components/CommGross/CommGrossQuotaBreakdownTable.jsx';
import CommGrossYearOverYearTable from '../components/CommGross/CommGrossYearOverYearTable.jsx';
import ExportCommGross from '../components/CommGross/ExportCommGross.jsx';
import MonthSelector from '../components/MonthSelector.jsx';
import RepSelector from '../components/RepSelector.jsx';
import YearSelector from '../components/YearSelector.jsx';

const IGNORE_REPS = ['SPLIT COMMISSIONS'];

function CommGross() {
  const [selectedMonth, setSelectedMonth] = React.useState();
  const [selectedRep, setSelectedRep] = React.useState('');
  const [selectedYear, setSelectedYear] = React.useState(new Date().getFullYear());

  const currentYear = React.useMemo(() => new Date().getFullYear(), []);

  const isContractAccounts = React.useMemo(
    () => selectedRep.includes('contract-accounts'),
    [selectedRep]
  );

  const isAllReps = React.useMemo(() => selectedRep === 'all-reps', [selectedRep]);

  const showQuotaBreakdown = React.useMemo(
    () => selectedRep && !isContractAccounts && !isAllReps,
    [selectedRep, isContractAccounts, isAllReps]
  );

  const showMonthlyCommissions = React.useMemo(
    () => selectedRep && !isContractAccounts && !isAllReps,
    [selectedRep, isContractAccounts, isAllReps]
  );

  const showMonthlyGrossSales = React.useMemo(
    () => selectedRep && !isContractAccounts,
    [selectedRep, isContractAccounts]
  );

  const ownsATerritory = React.useMemo(
    () => selectedRep && !isAllReps && !isContractAccounts && selectedRep !== 'Casey Johnson',
    [selectedRep, isAllReps, isContractAccounts]
  );

  const showSummarySection =
    showQuotaBreakdown ||
    showMonthlyCommissions ||
    showMonthlyGrossSales ||
    (ownsATerritory && selectedYear === currentYear);

  return (
    <Box sx={{ mx: '0.25rem', mb: '0.25rem', mt: '1rem' }}>
      <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
        <Typography variant="h4" data-cy="commgross-title">
          Gross Commissions
        </Typography>
        <RepSelector ignoreReps={IGNORE_REPS} onChange={setSelectedRep} selectedRep={selectedRep} />
        <YearSelector selectedYear={selectedYear} onChange={setSelectedYear} />
        <MonthSelector selectedMonth={selectedMonth} onChange={setSelectedMonth} />
        <ExportCommGross
          selectedMonth={selectedMonth}
          selectedRep={selectedRep}
          selectedYear={selectedYear}
        />
      </Stack>

      {showSummarySection && (
        <Grid container spacing={2} justifyContent="center" sx={{ mt: 3 }}>
          {showQuotaBreakdown && (
            <Grid size={6}>
              <CommGrossQuotaBreakdownTable selectedRep={selectedRep} selectedYear={selectedYear} />
            </Grid>
          )}
          {ownsATerritory && selectedYear === currentYear && (
            <Grid size={6}>
              <CommGrossYearOverYearTable selectedRep={selectedRep} selectedYear={selectedYear} />
            </Grid>
          )}
          {showMonthlyCommissions && (
            <Grid size={6}>
              <CommGrossMonthlyCommissionsTable
                selectedRep={selectedRep}
                selectedYear={selectedYear}
              />
            </Grid>
          )}
          {showMonthlyGrossSales && (
            <Grid size={6}>
              <CommGrossMonthlyGrossSalesTable
                selectedRep={selectedRep}
                selectedYear={selectedYear}
              />
            </Grid>
          )}
        </Grid>
      )}

      {selectedRep ? (
        <Box sx={{ mt: 3 }}>
          <CommGrossCommissionsTable
            selectedRep={selectedRep}
            selectedYear={selectedYear}
            selectedMonth={selectedMonth}
          />
        </Box>
      ) : (
        <Typography
          variant="h6"
          align="center"
          sx={{ mt: 4, color: 'text.secondary' }}
          data-cy="commgross-select-rep-message"
        >
          Please select a rep above
        </Typography>
      )}
    </Box>
  );
}

export default React.memo(CommGross);
